import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  signal,
  ViewChild,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import {
  FieldType,
  FieldTypeConfig,
  FormlyFieldConfig,
  FormlyModule,
} from '@ngx-formly/core';
import {
  FormlyFieldSelectProps,
  FormlySelectModule,
} from '@ngx-formly/core/select';
import { FormlyFieldProps } from '@ngx-formly/primeng/form-field';
import isEqual from 'lodash/isEqual';
import { ScrollerOptions } from 'primeng/api';
import { ChipModule } from 'primeng/chip';
import { InputTextModule } from 'primeng/inputtext';
import {
  MultiSelect,
  MultiSelectFilterEvent,
  MultiSelectLazyLoadEvent,
  MultiSelectModule,
} from 'primeng/multiselect';
import { SkeletonModule } from 'primeng/skeleton';
import {
  BehaviorSubject,
  debounceTime,
  distinctUntilChanged,
  filter,
  Observable,
  skip,
  startWith,
  Subject,
  Subscription,
  takeUntil,
} from 'rxjs';

interface MultiselectLazyProps
  extends FormlyFieldProps,
    FormlyFieldSelectProps {
  query: (
    perPage: number,
    page: number,
    search?: string,
    filters?: string,
  ) => Observable<{ data: object[]; meta: { total: number; to: number } }>;
  pageSize?: number;
  filterPlaceholder?: string;
}

interface MultiselectLazyItem {
  label?: string;
  value?: unknown;
  disabled?: boolean;
  status: 'pending' | 'loading' | 'loaded';
  skeletionWidth?: number; // for skeleton loader styling
}

export interface FormlyMultiselectLazyFieldConfig
  extends FormlyFieldConfig<MultiselectLazyProps> {}

@Component({
  selector: 'kon-multiselect-lazy-type',
  imports: [
    CommonModule,
    FormlyModule,
    FormlySelectModule,
    ReactiveFormsModule,
    InputTextModule,
    SkeletonModule,
    MultiSelectModule,
    ChipModule,
  ],
  template: `
    <p-multiSelect
      #select
      [id]="id"
      [appendTo]="'body'"
      [options]="(items$ | async) ?? []"
      [optionLabel]="'label'"
      [optionValue]="'value'"
      [placeholder]="props.placeholder"
      [formControl]="formControl"
      [showClear]="!props.required"
      (onChange)="props.change && props.change(field, $event)"
      [filter]="true"
      [autofocusFilter]="true"
      [resetFilterOnHide]="true"
      [style]="{ width: '100%' }"
      [virtualScroll]="true"
      [virtualScrollItemSize]="42"
      [virtualScrollOptions]="scrollerOptions"
      (onPanelShow)="onShow()"
      (onPanelHide)="onHide()"
      (onFilter)="onSearch($event)"
    >
      <ng-template let-items #selecteditems>
        @let opts = valueOptions();

        @if (opts.length > 0) {
          @if (values().length > 3) {
            <span>{{ values().length }} vybraných položiek</span>
          } @else {
            <div class="flex items-center gap-2 max-w-full grow">
              @for (item of opts; track item.value) {
                <p-chip
                  [label]="item.label"
                  [removable]="true"
                  (onRemove)="$event.stopPropagation(); remove(item.value)"
                />
              }
            </div>
          }
        }
      </ng-template>

      <ng-template let-item #item>
        <div class="flex items-center gap-2 max-w-full grow">
          @if (item.status === 'loaded') {
            <div class="grow truncate">{{ item.label }}</div>
          } @else {
            <p-skeleton
              class="w-full"
              [width]="(item.skeletonWidth ?? 50) + '%'"
              height="1rem"
            ></p-skeleton>
          }
        </div>
      </ng-template>
    </p-multiSelect>
  `,
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormlyMultiSelectLazyTypeComponent
  extends FieldType<FieldTypeConfig<MultiselectLazyProps>>
  implements AfterViewInit, OnDestroy
{
  @ViewChild('select') select!: MultiSelect;

  items$ = new BehaviorSubject<MultiselectLazyItem[]>([]);
  destroyed$ = new Subject<void>();
  search$ = new BehaviorSubject<string>('');
  scrollerOptions: ScrollerOptions = {
    showLoader: true,
    lazy: true,
    onLazyLoad: this.onLazyLoad.bind(this),
    scrollHeight: '220px',
  };
  lazyLoadTrigger$ = new Subject<MultiSelectLazyLoadEvent>();

  private lazyLoadSubscriptions: Subscription[] = [];
  private searchSubscription: Subscription = Subscription.EMPTY;
  private freshLoadSubscription: Subscription = Subscription.EMPTY;
  private valueSubscription: Subscription = Subscription.EMPTY;

  valueOptions = signal<MultiselectLazyItem[]>([]);
  values = signal<unknown[]>([]);

  ngAfterViewInit(): void {
    if (this.select?.filterService) {
      // this disables default filtering
      this.select.filterService.register('contains', () => true);
    }

    this.lazyLoadTrigger$
      .pipe(
        takeUntil(this.destroyed$),
        filter(({ first, last }) => {
          const allLoaded = this.items$.value
            .slice(first, last)
            .every((item) => item?.status === 'loaded');

          return !allLoaded;
        }),
      )
      .subscribe(({ first, last }) => {
        const firstPendingIndex = this.items$.value.findIndex(
          (item, index) =>
            index >= first && index < last && item?.status === 'pending',
        );

        const firstLoadedIndex = this.items$.value.findIndex(
          (item, index) =>
            index >= firstPendingIndex &&
            index < last &&
            item?.status === 'loaded',
        );

        if (firstPendingIndex === -1) {
          return;
        }

        // const itemsLimit = last - firstPendingIndex;
        const pageSize =
          firstLoadedIndex !== -1
            ? Math.min(firstLoadedIndex - firstPendingIndex, this.pageSize())
            : this.pageSize();
        const pageToLoad = Math.floor(firstPendingIndex / pageSize) + 1;

        const items = this.items$.value.slice(
          firstPendingIndex,
          firstPendingIndex + pageSize,
        );

        this.patchItems(
          firstPendingIndex,
          items.map((item) => ({
            ...this.mapItem(item),
            status: item.status === 'loaded' ? 'loaded' : 'loading', // don't set to loading if already loaded to prevent flickering
          })),
          this.items$.value.length,
        );

        const subscription = this.props
          .query(pageSize, pageToLoad, this.search$.value)
          .subscribe((response) => {
            this.patchItems(
              firstPendingIndex,
              response.data.map((item) => ({
                ...this.mapItem(item),
                status: 'loaded',
              })),
              response.meta.total,
            );
          });

        this.lazyLoadSubscriptions.push(subscription);
      });

    this.items$.next(this.generateItems(5));

    this.formControl.valueChanges
      .pipe(
        takeUntil(this.destroyed$),
        startWith(this.formControl.value),
        distinctUntilChanged(isEqual),
      )
      .subscribe((value) => {
        this.valueSubscription.unsubscribe();

        if (!value) {
          this.valueOptions.set([]);
          this.values.set([]);
          return;
        }

        const values = Array.isArray(value) ? value : [value];
        const valuesToLoad = [...values].splice(0, 3); // limit to 3 values to avoid large request headers - more than 3 values are not shown in the UI anyway
        const notLoadedValues: unknown[] = [];
        const valueOptions: MultiselectLazyItem[] = [];

        this.values.set(values);

        const preserveOriginalOrder = (
          a: MultiselectLazyItem,
          b: MultiselectLazyItem,
        ) => {
          const indexA = values.findIndex((val) => val === a.value);
          const indexB = values.findIndex((val) => val === b.value);
          return indexA - indexB;
        };

        for (const val of valuesToLoad) {
          const existingOption =
            this.items$.value.find(
              (item) => item.value === val && item.status === 'loaded',
            ) || this.valueOptions().find((item) => item.value === val);

          if (existingOption) {
            valueOptions.push({ ...existingOption });
          } else {
            notLoadedValues.push(val);
          }
        }

        if (notLoadedValues.length === 0) {
          this.valueOptions.set([...valueOptions].sort(preserveOriginalOrder));
          return;
        }

        const filter = notLoadedValues
          .map((val) => `${this.props.valueProp} eq "${val}"`)
          .join(' or ');

        this.valueSubscription = this.props
          .query(notLoadedValues.length, 1, undefined, filter)
          .subscribe((response) => {
            const loadedOptions: MultiselectLazyItem[] = response.data.map(
              (item) => ({
                ...this.mapItem(item),
                status: 'loaded',
              }),
            );

            this.valueOptions.set(
              [...valueOptions, ...loadedOptions].sort(preserveOriginalOrder),
            );
          });
      });
  }

  freshLoad(search = '') {
    this.select?.scroller?.scrollToIndex(0);
    this.search$.next(search);

    this.lazyLoadSubscriptions.forEach((sub) => sub.unsubscribe());
    this.lazyLoadSubscriptions = [];

    this.freshLoadSubscription.unsubscribe();
    this.freshLoadSubscription = this.props
      .query(this.pageSize(), 1, search)
      .subscribe((response) => {
        this.patchItems(
          0,
          response.data.map((item) => ({
            ...this.mapItem(item),
            status: 'loaded',
          })),
          response.meta.total,
        );

        this.select?.focusedOptionIndex.set(0); // focus first item
      });
  }

  async onLazyLoad(event: MultiSelectLazyLoadEvent) {
    this.lazyLoadTrigger$.next(event);
  }

  remove(value: unknown) {
    const currentValue = this.formControl.value
      ? Array.isArray(this.formControl.value)
        ? this.formControl.value
        : [this.formControl.value]
      : [];
    const newValue = currentValue.filter((val) => val !== value);
    this.formControl.setValue(newValue);
    this.formControl.markAsDirty();
  }

  onSearch(event: MultiSelectFilterEvent) {
    if (this.search$.value === event.filter) {
      return;
    }

    this.search$.next(event.filter);

    this.items$.next(
      this.generateItems(this.items$.value.length || 5), // reset to skeleton loaders while searching
    );
  }

  patchItems(
    start: number,
    items: Partial<MultiselectLazyItem>[],
    total: number,
  ) {
    let newItems = [...this.items$.value];

    if (newItems.length != total) {
      newItems = this.generateItems(total);
    }

    let index = start;

    for (const item of items) {
      newItems[index] = {
        ...newItems[index],
        ...item,
      };
      index++;
    }

    this.items$.next(newItems);
  }

  onShow() {
    this.freshLoad();
    this.searchSubscription.unsubscribe();
    this.searchSubscription = this.search$
      .pipe(skip(1), distinctUntilChanged(), debounceTime(500))
      .subscribe((search) => {
        this.freshLoad(search);
      });
  }

  onHide() {
    this.cleanupSubscriptions();
    this.search$.next('');
  }

  ngOnDestroy() {
    this.cleanupSubscriptions();
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  pageSize = () => this.props.pageSize ?? 50;

  mapItem(item: any): Partial<MultiselectLazyItem> {
    const label = this.getValue(item, this.props.labelProp ?? 'label') ?? '';
    const value = this.getValue(item, this.props.valueProp ?? 'value');
    const disabled = this.getValue(item, this.props.disabledProp ?? 'disabled');

    return {
      ...item,
      label,
      value,
      disabled,
    };
  }

  private getValue<T = any>(
    obj: any,
    getter: string | ((item: any) => T),
  ): T | undefined {
    if (typeof getter === 'string') {
      return obj[getter];
    } else if (typeof getter === 'function') {
      return getter(obj);
    }

    return undefined;
  }

  private cleanupSubscriptions() {
    this.lazyLoadSubscriptions.forEach((sub) => sub.unsubscribe());
    this.lazyLoadSubscriptions = [];
    this.searchSubscription.unsubscribe();
    this.freshLoadSubscription.unsubscribe();
    this.valueSubscription.unsubscribe();
  }

  private generateItems(total: number): MultiselectLazyItem[] {
    return Array.from<unknown, MultiselectLazyItem>({ length: total }, () => ({
      // disabled: true,
      status: 'pending',
      skeletonWidth: Math.floor(Math.random() * 41) + 40, // random width for skeleton from 40% to 80%
    }));
  }
}
